// Industry examples for landing page dashboard carousel + demo widget.
// All display strings are localized (en/es/fr). Structural data stays as-is.

import type { Language } from '../i18n';

// ── Types ──

type L10n = { en: string; es: string; fr: string };

function pick(obj: L10n, lang: Language): string {
  return obj[lang] || obj.en;
}

export interface RepairItem {
  id: string;
  device: L10n;
  status: 'inProgress' | 'diagnosing' | 'waitingParts';
  tech: string;
}

export interface DashboardData {
  title: L10n;
  repairs: RepairItem[];
  metrics: { received: number; delivered: number; pending: number };
  technicians: { active: number; total: number };
  alerts: { pendingParts: number; delayed: number; overdue: number };
  activity: Array<{ time: string; actionKey: string; detail: L10n }>;
}

export interface DemoData {
  code: string;
  device: L10n;
  status: L10n;
  received: string;
  estimated: string;
  technician: string;
  note: L10n;
}

export interface IndustryExample {
  id: string;
  name: L10n;
  dashboard: DashboardData;
  demo: DemoData;
}

// ── Shared state (module-level pub/sub) ──

let currentIndex = 0;
const listeners: Array<(index: number) => void> = [];

export function getCurrentIndustryIndex(): number {
  return currentIndex;
}

export function setCurrentIndustryIndex(index: number): void {
  currentIndex = index;
  listeners.forEach((fn) => fn(index));
}

export function onIndustryChange(fn: (index: number) => void): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

// ── 10 Localized Industry Examples ──

const RAW_EXAMPLES: IndustryExample[] = [
  {
    id: 'mobile-repair',
    name: { en: 'Mobile Device Repair', es: 'Reparación de Dispositivos Móviles', fr: 'Réparation d\'appareils mobiles' },
    dashboard: {
      title: { en: 'SmartFix — Phone & Tablet Repair', es: 'SmartFix — Celulares y Tablets', fr: 'SmartFix — Téléphones et tablettes' },
      repairs: [
        { id: 'REP-2847', device: { en: 'iPhone 14 Pro — Screen', es: 'iPhone 14 Pro — Pantalla', fr: 'iPhone 14 Pro — Écran' }, status: 'inProgress', tech: 'Carlos M.' },
        { id: 'REP-2845', device: { en: 'Samsung S23 — Battery', es: 'Samsung S23 — Batería', fr: 'Samsung S23 — Batterie' }, status: 'diagnosing', tech: 'Ana R.' },
        { id: 'REP-2842', device: { en: 'iPad Pro 11" — USB-C port', es: 'iPad Pro 11" — Puerto USB-C', fr: 'iPad Pro 11" — Port USB-C' }, status: 'waitingParts', tech: 'Carlos M.' },
        { id: 'REP-2839', device: { en: 'Pixel 8 — Camera lens', es: 'Pixel 8 — Lente de cámara', fr: 'Pixel 8 — Objectif caméra' }, status: 'inProgress', tech: 'Miguel S.' },
      ],
      metrics: { received: 12, delivered: 8, pending: 4 },
      technicians: { active: 3, total: 5 },
      alerts: { pendingParts: 2, delayed: 1, overdue: 0 },
      activity: [
        { time: '09:42', actionKey: 'completedRepair', detail: { en: 'iPhone 14 Pro (REP-2847)', es: 'iPhone 14 Pro (REP-2847)', fr: 'iPhone 14 Pro (REP-2847)' } },
        { time: '09:15', actionKey: 'receivedEquipment', detail: { en: 'Pixel 8 — Camera lens', es: 'Pixel 8 — Lente de cámara', fr: 'Pixel 8 — Objectif caméra' } },
        { time: '08:30', actionKey: 'customerNotified', detail: { en: 'REP-2839 ready for pickup', es: 'REP-2839 listo para recoger', fr: 'REP-2839 prêt pour retrait' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Carlos, Ana, Miguel', es: 'Carlos, Ana, Miguel', fr: 'Carlos, Ana, Miguel' } },
      ],
    },
    demo: {
      code: 'REP-DEMO',
      device: { en: 'iPhone 14 Pro', es: 'iPhone 14 Pro', fr: 'iPhone 14 Pro' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'Today, 9:30 AM',
      estimated: 'Tomorrow, 2:00 PM',
      technician: 'Carlos M.',
      note: {
        en: 'Screen replacement in progress. OEM part installed. 60 min remaining.',
        es: 'Reemplazo de pantalla en progreso. Pieza original instalada. 60 min restantes.',
        fr: 'Remplacement d\'écran en cours. Pièce d\'origine installée. 60 min restantes.',
      },
    },
  },

  {
    id: 'auto-transmission',
    name: { en: 'Automotive Transmission', es: 'Transmisiones Automotrices', fr: 'Transmissions automobiles' },
    dashboard: {
      title: { en: 'QuickShift — Transmission Shop', es: 'Transmisiones Rápidas — Taller Automotriz', fr: 'QuickShift — Atelier de transmissions' },
      repairs: [
        { id: 'ORD-1123', device: { en: 'Toyota Corolla — AT Gearbox', es: 'Toyota Corolla — Caja AT', fr: 'Toyota Corolla — Boîte AT' }, status: 'inProgress', tech: 'Héctor V.' },
        { id: 'ORD-1120', device: { en: 'Honda CR-V — Clutch kit', es: 'Honda CR-V — Kit embrague', fr: 'Honda CR-V — Kit d\'embrayage' }, status: 'diagnosing', tech: 'Luis T.' },
        { id: 'ORD-1118', device: { en: 'Nissan Versa — Differential', es: 'Nissan Versa — Diferencial', fr: 'Nissan Versa — Différentiel' }, status: 'waitingParts', tech: 'Héctor V.' },
        { id: 'ORD-1115', device: { en: 'Ford Ranger — Torque converter', es: 'Ford Ranger — Convertidor', fr: 'Ford Ranger — Convertisseur' }, status: 'inProgress', tech: 'Ramón G.' },
      ],
      metrics: { received: 7, delivered: 4, pending: 3 },
      technicians: { active: 4, total: 6 },
      alerts: { pendingParts: 1, delayed: 2, overdue: 1 },
      activity: [
        { time: '10:15', actionKey: 'completedRepair', detail: { en: 'Ford Ranger converter (ORD-1115)', es: 'Ford Ranger convertidor (ORD-1115)', fr: 'Ford Ranger convertisseur (ORD-1115)' } },
        { time: '09:30', actionKey: 'partsArrived', detail: { en: 'Nissan Versa differential', es: 'Nissan Versa diferencial', fr: 'Nissan Versa différentiel' } },
        { time: '08:45', actionKey: 'receivedEquipment', detail: { en: 'Hyundai Tucson — CVT', es: 'Hyundai Tucson — Transmisión CVT', fr: 'Hyundai Tucson — CVT' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Héctor, Luis, Ramón, Diego', es: 'Héctor, Luis, Ramón, Diego', fr: 'Héctor, Luis, Ramón, Diego' } },
      ],
    },
    demo: {
      code: 'ORD-DEMO',
      device: { en: 'Toyota Corolla 2022 — AT Transmission', es: 'Toyota Corolla 2022 — Transmisión AT', fr: 'Toyota Corolla 2022 — Transmission AT' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 28, 10:15 AM',
      estimated: 'June 1, 5:00 PM',
      technician: 'Héctor V.',
      note: {
        en: 'Full disassembly. Torque converter replaced. Reassembly in progress.',
        es: 'Desmontaje completo. Convertidor de par reemplazado. En fase de reinstalación.',
        fr: 'Démontage complet. Convertisseur de couple remplacé. Remontage en cours.',
      },
    },
  },

  {
    id: 'motorcycle-repair',
    name: { en: 'Motorcycle Workshop', es: 'Taller de Motocicletas', fr: 'Atelier de motos' },
    dashboard: {
      title: { en: 'MotoService — Bike Repair', es: 'MotoService — Taller de Motos', fr: 'MotoService — Réparation motos' },
      repairs: [
        { id: 'MTO-0562', device: { en: 'Yamaha MT-07 — Engine', es: 'Yamaha MT-07 — Motor', fr: 'Yamaha MT-07 — Moteur' }, status: 'inProgress', tech: 'Diego R.' },
        { id: 'MTO-0559', device: { en: 'Honda CB500 — ABS brakes', es: 'Honda CB500 — Frenos ABS', fr: 'Honda CB500 — Freins ABS' }, status: 'diagnosing', tech: 'Marco A.' },
        { id: 'MTO-0557', device: { en: 'Kawasaki Ninja — Suspension', es: 'Kawasaki Ninja — Suspensión', fr: 'Kawasaki Ninja — Suspension' }, status: 'waitingParts', tech: 'Diego R.' },
        { id: 'MTO-0554', device: { en: 'Suzuki GSX — Chain kit', es: 'Suzuki GSX — Kit cadena', fr: 'Suzuki GSX — Kit chaîne' }, status: 'inProgress', tech: 'Omar S.' },
      ],
      metrics: { received: 9, delivered: 6, pending: 3 },
      technicians: { active: 3, total: 4 },
      alerts: { pendingParts: 1, delayed: 0, overdue: 1 },
      activity: [
        { time: '10:30', actionKey: 'completedRepair', detail: { en: 'Suzuki GSX chain (MTO-0554)', es: 'Suzuki GSX cadena (MTO-0554)', fr: 'Suzuki GSX chaîne (MTO-0554)' } },
        { time: '09:45', actionKey: 'receivedEquipment', detail: { en: 'Ducati Monster — oil & filters', es: 'Ducati Monster — aceite y filtros', fr: 'Ducati Monster — huile et filtres' } },
        { time: '09:00', actionKey: 'customerNotified', detail: { en: 'MTO-0554 ready for pickup', es: 'MTO-0554 listo para recoger', fr: 'MTO-0554 prêt pour retrait' } },
        { time: '08:15', actionKey: 'shiftStarted', detail: { en: 'Diego, Marco, Omar', es: 'Diego, Marco, Omar', fr: 'Diego, Marco, Omar' } },
      ],
    },
    demo: {
      code: 'MTO-DEMO',
      device: { en: 'Yamaha MT-07 2023 — Engine inspection', es: 'Yamaha MT-07 2023 — Revisión de motor', fr: 'Yamaha MT-07 2023 — Inspection moteur' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 29, 8:30 AM',
      estimated: 'May 31, 12:00 PM',
      technician: 'Diego R.',
      note: {
        en: 'Valve adjustment and gasket replacement. Waiting for head gasket.',
        es: 'Ajuste de válvulas y cambio de empaques. Esperando junta de culata.',
        fr: 'Réglage des soupapes et remplacement des joints. En attente du joint de culasse.',
      },
    },
  },

  {
    id: 'lawn-equipment',
    name: { en: 'Lawn & Garden Equipment', es: 'Equipo de Jardinería', fr: 'Équipement de jardinage' },
    dashboard: {
      title: { en: 'GreenTech — Garden Equipment', es: 'GreenTech — Equipo de Jardinería', fr: 'GreenTech — Équipement de jardin' },
      repairs: [
        { id: 'JRD-0312', device: { en: 'Honda HRX — Lawn mower', es: 'Honda HRX — Podadora', fr: 'Honda HRX — Tondeuse' }, status: 'inProgress', tech: 'Pedro N.' },
        { id: 'JRD-0309', device: { en: 'Stihl MS 250 — Chainsaw', es: 'Stihl MS 250 — Motosierra', fr: 'Stihl MS 250 — Tronçonneuse' }, status: 'diagnosing', tech: 'Tomás G.' },
        { id: 'JRD-0307', device: { en: 'Echo PB-770 — Leaf blower', es: 'Echo PB-770 — Sopladora', fr: 'Echo PB-770 — Souffleur' }, status: 'waitingParts', tech: 'Pedro N.' },
        { id: 'JRD-0304', device: { en: 'Husqvarna 450 — Trimmer', es: 'Husqvarna 450 — Desbrozadora', fr: 'Husqvarna 450 — Débroussailleuse' }, status: 'inProgress', tech: 'Javier L.' },
      ],
      metrics: { received: 5, delivered: 3, pending: 2 },
      technicians: { active: 2, total: 3 },
      alerts: { pendingParts: 2, delayed: 1, overdue: 0 },
      activity: [
        { time: '09:20', actionKey: 'completedRepair', detail: { en: 'Husqvarna 450 carburetor', es: 'Husqvarna 450 carburador', fr: 'Husqvarna 450 carburateur' } },
        { time: '08:45', actionKey: 'partsArrived', detail: { en: 'Stihl MS 250 — spark plug & filter', es: 'Stihl MS 250 — bujía y filtro', fr: 'Stihl MS 250 — bougie et filtre' } },
        { time: '08:10', actionKey: 'receivedEquipment', detail: { en: 'Toro TimeMaster — blades', es: 'Toro TimeMaster — cuchillas', fr: 'Toro TimeMaster — lames' } },
        { time: '07:30', actionKey: 'shiftStarted', detail: { en: 'Pedro, Tomás, Javier', es: 'Pedro, Tomás, Javier', fr: 'Pedro, Tomás, Javier' } },
      ],
    },
    demo: {
      code: 'JRD-DEMO',
      device: { en: 'Honda HRX 2024 — Self-propelled mower', es: 'Honda HRX 2024 — Podadora autopropulsada', fr: 'Honda HRX 2024 — Tondeuse autopropulsée' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 27, 9:00 AM',
      estimated: 'May 30, 3:00 PM',
      technician: 'Pedro N.',
      note: {
        en: 'Blade replacement and carburetor adjustment. Waiting for maintenance kit.',
        es: 'Cambio de cuchillas y ajuste de carburador. Esperando kit de mantenimiento.',
        fr: 'Remplacement des lames et réglage du carburateur. En attente du kit d\'entretien.',
      },
    },
  },

  {
    id: 'appliance-repair',
    name: { en: 'Home Appliance Service', es: 'Electrodomésticos', fr: 'Appareils électroménagers' },
    dashboard: {
      title: { en: 'ElectroFix — Home Appliances', es: 'ElectroFix — Electrodomésticos', fr: 'ElectroFix — Électroménagers' },
      repairs: [
        { id: 'ELC-0812', device: { en: 'Samsung — Washer 12kg', es: 'Samsung — Lavadora 12kg', fr: 'Samsung — Lave-linge 12kg' }, status: 'inProgress', tech: 'Rosa M.' },
        { id: 'ELC-0809', device: { en: 'LG — Side-by-Side Fridge', es: 'LG — Refrigerador Side-by-Side', fr: 'LG — Réfrigérateur Side-by-Side' }, status: 'diagnosing', tech: 'Andrés C.' },
        { id: 'ELC-0807', device: { en: 'Whirlpool — Dryer', es: 'Whirlpool — Secadora', fr: 'Whirlpool — Sèche-linge' }, status: 'waitingParts', tech: 'Rosa M.' },
        { id: 'ELC-0804', device: { en: 'Mabe — Built-in stove', es: 'Mabe — Estufa empotrable', fr: 'Mabe — Cuisinière encastrable' }, status: 'inProgress', tech: 'Andrés C.' },
      ],
      metrics: { received: 8, delivered: 5, pending: 3 },
      technicians: { active: 2, total: 4 },
      alerts: { pendingParts: 1, delayed: 1, overdue: 0 },
      activity: [
        { time: '10:00', actionKey: 'completedRepair', detail: { en: 'Mabe stove — ignition (ELC-0804)', es: 'Mabe estufa — ignición (ELC-0804)', fr: 'Mabe cuisinière — allumage (ELC-0804)' } },
        { time: '09:15', actionKey: 'onSiteVisit', detail: { en: 'LG fridge — diagnostics', es: 'LG refrigerador — diagnóstico', fr: 'LG réfrigérateur — diagnostic' } },
        { time: '08:30', actionKey: 'customerNotified', detail: { en: 'ELC-0804 ready for delivery', es: 'ELC-0804 listo para entrega', fr: 'ELC-0804 prêt pour livraison' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Rosa, Andrés', es: 'Rosa, Andrés', fr: 'Rosa, Andrés' } },
      ],
    },
    demo: {
      code: 'ELC-DEMO',
      device: { en: 'Samsung WA12CG — 12kg Washer', es: 'Samsung WA12CG — Lavadora 12kg', fr: 'Samsung WA12CG — Lave-linge 12kg' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 28, 11:00 AM',
      estimated: 'May 30, 6:00 PM',
      technician: 'Rosa M.',
      note: {
        en: 'Drain pump replacement. Original Samsung part in transit.',
        es: 'Reemplazo de bomba de desagüe. Pieza original Samsung en camino.',
        fr: 'Remplacement de la pompe de vidange. Pièce d\'origine Samsung en transit.',
      },
    },
  },

  {
    id: 'bicycle-repair',
    name: { en: 'Bicycle Workshop', es: 'Taller de Bicicletas', fr: 'Atelier de vélos' },
    dashboard: {
      title: { en: 'BikeLab — Bicycle Repair', es: 'BikeLab — Taller de Bicicletas', fr: 'BikeLab — Réparation de vélos' },
      repairs: [
        { id: 'BIK-0456', device: { en: 'Trek Domane — Drivetrain', es: 'Trek Domane — Transmisión', fr: 'Trek Domane — Transmission' }, status: 'inProgress', tech: 'Julián F.' },
        { id: 'BIK-0453', device: { en: 'Specialized Rockhopper — Brakes', es: 'Specialized Rockhopper — Frenos', fr: 'Specialized Rockhopper — Freins' }, status: 'diagnosing', tech: 'Nadia P.' },
        { id: 'BIK-0451', device: { en: 'Cannondale Synapse — Wheels', es: 'Cannondale Synapse — Ruedas', fr: 'Cannondale Synapse — Roues' }, status: 'waitingParts', tech: 'Julián F.' },
        { id: 'BIK-0449', device: { en: 'Giant TCR — Carbon frame', es: 'Giant TCR — Cuadro carbono', fr: 'Giant TCR — Cadre carbone' }, status: 'inProgress', tech: 'Nadia P.' },
      ],
      metrics: { received: 14, delivered: 10, pending: 4 },
      technicians: { active: 2, total: 3 },
      alerts: { pendingParts: 1, delayed: 0, overdue: 0 },
      activity: [
        { time: '10:45', actionKey: 'completedRepair', detail: { en: 'Giant TCR carbon inspection', es: 'Giant TCR inspección carbono', fr: 'Giant TCR inspection carbone' } },
        { time: '09:50', actionKey: 'receivedEquipment', detail: { en: 'Santa Cruz Hightower — suspension', es: 'Santa Cruz Hightower — suspensión', fr: 'Santa Cruz Hightower — suspension' } },
        { time: '09:00', actionKey: 'customerNotified', detail: { en: 'BIK-0449 ready for pickup', es: 'BIK-0449 listo para recoger', fr: 'BIK-0449 prêt pour retrait' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Julián, Nadia', es: 'Julián, Nadia', fr: 'Julián, Nadia' } },
      ],
    },
    demo: {
      code: 'BIK-DEMO',
      device: { en: 'Trek Domane SL6 2023 — Drivetrain tune', es: 'Trek Domane SL6 2023 — Ajuste de transmisión', fr: 'Trek Domane SL6 2023 — Réglage transmission' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 29, 9:15 AM',
      estimated: 'May 30, 1:00 PM',
      technician: 'Julián F.',
      note: {
        en: 'Cassette and chain replacement. Shimano 105 indexing in progress.',
        es: 'Cambio de cassette y cadena Shimano 105. Indexado de cambios en progreso.',
        fr: 'Remplacement cassette et chaîne Shimano 105. Indexation en cours.',
      },
    },
  },

  {
    id: 'watch-jewelry',
    name: { en: 'Watch & Jewelry Repair', es: 'Relojería y Joyería', fr: 'Horlogerie et bijouterie' },
    dashboard: {
      title: { en: 'Precision Time — Fine Watches', es: 'Precision Time — Relojería Fina', fr: 'Precision Time — Haute horlogerie' },
      repairs: [
        { id: 'WTC-0198', device: { en: 'Rolex Datejust — Full service', es: 'Rolex Datejust — Servicio completo', fr: 'Rolex Datejust — Service complet' }, status: 'inProgress', tech: 'Sofía L.' },
        { id: 'WTC-0195', device: { en: 'Omega Seamaster — Crystal', es: 'Omega Seamaster — Cristal', fr: 'Omega Seamaster — Verre' }, status: 'diagnosing', tech: 'Mateo D.' },
        { id: 'WTC-0193', device: { en: 'Cartier Tank — Strap', es: 'Cartier Tank — Correa', fr: 'Cartier Tank — Bracelet' }, status: 'waitingParts', tech: 'Sofía L.' },
        { id: 'WTC-0190', device: { en: 'Tag Heuer — Chronograph', es: 'Tag Heuer — Cronógrafo', fr: 'Tag Heuer — Chronographe' }, status: 'inProgress', tech: 'Mateo D.' },
      ],
      metrics: { received: 4, delivered: 2, pending: 2 },
      technicians: { active: 2, total: 2 },
      alerts: { pendingParts: 1, delayed: 0, overdue: 0 },
      activity: [
        { time: '11:00', actionKey: 'completedRepair', detail: { en: 'Tag Heuer chronograph (WTC-0190)', es: 'Tag Heuer cronógrafo (WTC-0190)', fr: 'Tag Heuer chronographe (WTC-0190)' } },
        { time: '10:15', actionKey: 'partsArrived', detail: { en: 'Cartier — original leather strap', es: 'Cartier — correa original piel', fr: 'Cartier — bracelet cuir d\'origine' } },
        { time: '09:30', actionKey: 'receivedEquipment', detail: { en: 'Patek Philippe — general inspection', es: 'Patek Philippe — revisión general', fr: 'Patek Philippe — inspection générale' } },
        { time: '08:30', actionKey: 'shiftStarted', detail: { en: 'Sofía, Mateo', es: 'Sofía, Mateo', fr: 'Sofía, Mateo' } },
      ],
    },
    demo: {
      code: 'WTC-DEMO',
      device: { en: 'Rolex Datejust 36mm — Full service', es: 'Rolex Datejust 36mm — Servicio completo', fr: 'Rolex Datejust 36mm — Service complet' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 26, 10:00 AM',
      estimated: 'June 4, 6:00 PM',
      technician: 'Sofía L.',
      note: {
        en: 'Disassembly, ultrasonic cleaning and lubrication. Movement calibration in progress.',
        es: 'Desmontaje, limpieza ultrasónica y lubricación. Calibración de movimiento en progreso.',
        fr: 'Démontage, nettoyage ultrasonique et lubrification. Calibration du mouvement en cours.',
      },
    },
  },

  {
    id: 'computer-repair',
    name: { en: 'Computer & Laptop Service', es: 'Reparación de Cómputo', fr: 'Service informatique' },
    dashboard: {
      title: { en: 'ByteCare — Computer Repair', es: 'ByteCare — Reparación de Cómputo', fr: 'ByteCare — Réparation informatique' },
      repairs: [
        { id: 'CMP-0723', device: { en: 'MacBook Pro M2 — Display', es: 'MacBook Pro M2 — Pantalla', fr: 'MacBook Pro M2 — Écran' }, status: 'inProgress', tech: 'Daniel V.' },
        { id: 'CMP-0720', device: { en: 'Dell XPS 15 — Battery', es: 'Dell XPS 15 — Batería', fr: 'Dell XPS 15 — Batterie' }, status: 'diagnosing', tech: 'Karla R.' },
        { id: 'CMP-0718', device: { en: 'ThinkPad T14 — Keyboard', es: 'ThinkPad T14 — Teclado', fr: 'ThinkPad T14 — Clavier' }, status: 'waitingParts', tech: 'Daniel V.' },
        { id: 'CMP-0715', device: { en: 'HP Spectre — SSD upgrade', es: 'HP Spectre — Actualización SSD', fr: 'HP Spectre — Mise à niveau SSD' }, status: 'inProgress', tech: 'Karla R.' },
      ],
      metrics: { received: 10, delivered: 7, pending: 3 },
      technicians: { active: 3, total: 4 },
      alerts: { pendingParts: 1, delayed: 1, overdue: 0 },
      activity: [
        { time: '10:20', actionKey: 'completedRepair', detail: { en: 'HP Spectre SSD upgrade (CMP-0715)', es: 'HP Spectre SSD (CMP-0715)', fr: 'HP Spectre SSD (CMP-0715)' } },
        { time: '09:30', actionKey: 'diagnosticRun', detail: { en: 'Dell XPS — battery test', es: 'Dell XPS — prueba de batería', fr: 'Dell XPS — test de batterie' } },
        { time: '08:45', actionKey: 'customerNotified', detail: { en: 'CMP-0715 ready for pickup', es: 'CMP-0715 listo para recoger', fr: 'CMP-0715 prêt pour retrait' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Daniel, Karla, Francisco', es: 'Daniel, Karla, Francisco', fr: 'Daniel, Karla, Francisco' } },
      ],
    },
    demo: {
      code: 'CMP-DEMO',
      device: { en: 'MacBook Pro M2 2023 — Display replacement', es: 'MacBook Pro M2 2023 — Reemplazo de pantalla', fr: 'MacBook Pro M2 2023 — Remplacement d\'écran' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 29, 11:30 AM',
      estimated: 'May 31, 3:00 PM',
      technician: 'Daniel V.',
      note: {
        en: 'Original Retina display being installed. Color calibration pending.',
        es: 'Pantalla retina original en instalación. Calibración de color pendiente.',
        fr: 'Écran Retina d\'origine en cours d\'installation. Calibration des couleurs en attente.',
      },
    },
  },

  {
    id: 'musical-instruments',
    name: { en: 'Musical Instrument Repair', es: 'Instrumentos Musicales', fr: 'Instruments de musique' },
    dashboard: {
      title: { en: 'Perfect Sound — Instruments', es: 'Sonido Perfecto — Instrumentos', fr: 'Son Parfait — Instruments' },
      repairs: [
        { id: 'INS-0153', device: { en: 'Fender Stratocaster — Electronics', es: 'Fender Stratocaster — Electrónica', fr: 'Fender Stratocaster — Électronique' }, status: 'inProgress', tech: 'Emilio R.' },
        { id: 'INS-0150', device: { en: 'Yamaha Alto Sax — Keys', es: 'Yamaha Saxofón Alto — Llaves', fr: 'Yamaha Saxo Alto — Clés' }, status: 'diagnosing', tech: 'Lucía F.' },
        { id: 'INS-0148', device: { en: 'Pearl Drum Kit — Heads', es: 'Pearl Batería — Parches', fr: 'Pearl Batterie — Peaux' }, status: 'waitingParts', tech: 'Emilio R.' },
        { id: 'INS-0145', device: { en: 'Steinway Upright — Tuning', es: 'Steinway Piano Vertical — Afinación', fr: 'Steinway Piano Droit — Accordage' }, status: 'inProgress', tech: 'Lucía F.' },
      ],
      metrics: { received: 6, delivered: 3, pending: 3 },
      technicians: { active: 2, total: 3 },
      alerts: { pendingParts: 1, delayed: 0, overdue: 1 },
      activity: [
        { time: '10:50', actionKey: 'completedRepair', detail: { en: 'Steinway tuning (INS-0145)', es: 'Steinway afinación (INS-0145)', fr: 'Steinway accordage (INS-0145)' } },
        { time: '09:30', actionKey: 'receivedEquipment', detail: { en: 'Gibson Les Paul — frets', es: 'Gibson Les Paul — trastes', fr: 'Gibson Les Paul — frettes' } },
        { time: '08:45', actionKey: 'customerNotified', detail: { en: 'INS-0145 ready for pickup', es: 'INS-0145 listo para recoger', fr: 'INS-0145 prêt pour retrait' } },
        { time: '08:00', actionKey: 'shiftStarted', detail: { en: 'Emilio, Lucía', es: 'Emilio, Lucía', fr: 'Emilio, Lucía' } },
      ],
    },
    demo: {
      code: 'INS-DEMO',
      device: { en: 'Fender Stratocaster American Pro II', es: 'Fender Stratocaster American Pro II', fr: 'Fender Stratocaster American Pro II' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 28, 2:00 PM',
      estimated: 'June 2, 12:00 PM',
      technician: 'Emilio R.',
      note: {
        en: 'Pickup and potentiometer replacement. Point-to-point wiring in progress.',
        es: 'Reemplazo de pastillas y potenciómetros. Cableado punto a punto en progreso.',
        fr: 'Remplacement des micros et potentiomètres. Câblage point à point en cours.',
      },
    },
  },

  {
    id: 'medical-equipment',
    name: { en: 'Medical Equipment Service', es: 'Equipo Médico', fr: 'Équipement médical' },
    dashboard: {
      title: { en: 'MediTech Service — Medical Eq.', es: 'MediTech Service — Equipo Médico', fr: 'MediTech Service — Équipement médical' },
      repairs: [
        { id: 'MED-0234', device: { en: 'GE Ultrasound Vscan — Probe', es: 'GE Ultrasound Vscan — Sonda', fr: 'GE Ultrasound Vscan — Sonde' }, status: 'inProgress', tech: 'Carmen G.' },
        { id: 'MED-0231', device: { en: 'Philips Monitor — Display', es: 'Philips Monitor — Pantalla', fr: 'Philips Monitor — Écran' }, status: 'diagnosing', tech: 'Roberto S.' },
        { id: 'MED-0229', device: { en: 'Mindray Ventilator — Sensor', es: 'Mindray Ventilador — Sensor', fr: 'Mindray Ventilateur — Capteur' }, status: 'waitingParts', tech: 'Carmen G.' },
        { id: 'MED-0226', device: { en: 'Siemens EKG — Electrodes', es: 'Siemens EKG — Electrodos', fr: 'Siemens EKG — Électrodes' }, status: 'inProgress', tech: 'Roberto S.' },
      ],
      metrics: { received: 3, delivered: 2, pending: 1 },
      technicians: { active: 2, total: 3 },
      alerts: { pendingParts: 1, delayed: 0, overdue: 0 },
      activity: [
        { time: '09:15', actionKey: 'completedRepair', detail: { en: 'Siemens EKG calibration (MED-0226)', es: 'Siemens EKG calibración (MED-0226)', fr: 'Siemens EKG calibration (MED-0226)' } },
        { time: '08:30', actionKey: 'calibrationDone', detail: { en: 'GE Ultrasound — verification', es: 'GE Ultrasound — verificación', fr: 'GE Ultrasound — vérification' } },
        { time: '08:00', actionKey: 'urgentRequest', detail: { en: 'Philips monitor — Pediatric ICU', es: 'Philips monitor — UCI pediátrica', fr: 'Philips monitor — USI pédiatrique' } },
        { time: '07:30', actionKey: 'shiftStarted', detail: { en: 'Carmen, Roberto', es: 'Carmen, Roberto', fr: 'Carmen, Roberto' } },
      ],
    },
    demo: {
      code: 'MED-DEMO',
      device: { en: 'GE Healthcare Vscan Air — Wireless probe', es: 'GE Healthcare Vscan Air — Sonda inalámbrica', fr: 'GE Healthcare Vscan Air — Sonde sans fil' },
      status: { en: 'In repair', es: 'En reparación', fr: 'En réparation' },
      received: 'May 29, 7:45 AM',
      estimated: 'May 30, 11:00 AM',
      technician: 'Carmen G.',
      note: {
        en: 'Piezoelectric crystal replacement. Image quality test in progress.',
        es: 'Reemplazo de cristal piezoeléctrico. Prueba de imagen en progreso.',
        fr: 'Remplacement du cristal piézoélectrique. Test de qualité d\'image en cours.',
      },
    },
  },
];

// ── Public API ──

export const industryExamples: IndustryExample[] = RAW_EXAMPLES;

export function getLocalizedExamples(lang: Language): IndustryExample[] {
  return RAW_EXAMPLES;
}

export function getIndustryExample(index: number): IndustryExample {
  return RAW_EXAMPLES[index % RAW_EXAMPLES.length];
}

/** Resolve a localized string. Use in components: pick(example.name, language) */
export { pick as localize };
